<template>
    <FocusLock :returnfocus="false" :disabled="activeResult === result">
    <div class="grid-x" v-if="draft && labels">
        <a tabindex="0" @click="close" @keyup.enter="close" class="icon-cross-w accordion__remove" v-if="canEdit"></a>
        <div class="head head--blue cell">
            <div class="form--edit-study">
                <div class="form__result-head">
                    <div class="form__head-item">
                        {{labels.investigationPeriod}}
                    </div>
                    <div class="form__head-item">
                        <select name="startYear" 
                            class="form__select" 
                            v-model="draft.startYear.selected" :disabled="!canEdit"
                            :class="{'input--invalid': showInvalid('startYear') }"
                             v-validate.initial="'required'"
                             :data-vv-as="labels.startYearLabel">
                            <option disabled selected :value="null">{{labels.startYear}}</option>
                            <option v-for="option in draft.startYear.options" :key="option.id" :value="option.id">{{ option.name }}</option>
                        </select>
                    </div>
                    <div class="form__head-item">
                        &nbsp;-&nbsp;
                    </div>
                    <div class="form__head-item">
                        <select name="endYear" 
                            class="form__select" 
                            :class="{'input--invalid': showInvalid('endYear') }"
                            v-model="draft.endYear.selected" 
                            :disabled="!canEdit"
                            v-validate.initial="'required'"
                            :data-vv-as="labels.endYearLabel">
                            <option disabled selected :value="null">{{labels.endYear}}</option>
                            <option v-for="option in draft.endYear.options" :key="option.id" :value="option.id">{{ option.name }}</option>
                        </select>
                        <!--<small class="form__head-error" v-if="fields.endYear && fields.endYear.dirty">Slut år skal være højere start år.</small>-->
                    </div>
                    <div class="form__head-item">
                        {{labels.numberOfObservations}}
                        <input type="number" v-model="draft.observations" :disabled="!canEdit" />
                    </div>
                    <div class="form__head-item">
                        {{labels.numberOfParticipants}}
                        <input type="number" v-model="draft.participants" :disabled="!canEdit" />
                    </div>
                </div>
            </div>
        </div>
        <div class="form--edit-study content content--gray form__divider cell">
            <h2 class="h1 text-center">{{labels.resultSummary}}</h2>
            <div class="grid-x">
                <div class="form__language-column large-6 cell">
                    <h3>{{labels.danish}}</h3>
                    <div class="form__item">
                        <textarea rows="6" 
                            :disabled="!canEdit"
                            name="Resultat verbalt" 
                            id="comment_dk" 
                            v-model="draft.comment[1].text" 
                            :class="{'input--invalid': showInvalid('Resultat verbalt') }"
                            maxlength="2000"
                            v-floatable>
                        </textarea>
                        <label for="comment_dk" class="floating-label">{{labels.resultVerbally}}* {{ counter(draft.comment[1].text.length, 2000) }}</label>
                    </div>
                </div>
                <div class="form__language-column large-6 cell">
                    <h3>{{labels.english}}</h3>
                    <div class="form__item">
                        <textarea rows="6" 
                            :disabled="!canEdit"
                            name="Result verbally" 
                            id="comment_en" 
                            v-model="draft.comment[0].text" 
                            :class="{'input--invalid': showInvalid('Result verbally') }"
                            maxlength="2000"
                            v-floatable spellcheck="false">
                        </textarea>
                        <label for="comment_en" class="floating-label">{{labels.resultVerbally}}* {{ counter(draft.comment[0].text.length, 2000) }}</label>
                    </div>
                </div>
            </div>
        </div>
        <div class="cell content--white">
            <h2 class="h1 text-center">{{labels.targetGroupsEffectsAndInterventions}}</h2>
            <a tabindex="0" @click="toggleAll(true)" @keyup.enter="toggleAll(true)" v-if="openAccordions.length < 4" class="accordion__toggle">
                <span class="icon-plus"></span>
                    {{labels.unfoldAll}}
            </a>
            <a tabindex="0" @click="toggleAll(false)" @keyup.enter="toggleAll(false)" v-if="openAccordions.length > 3" class="accordion__toggle">
                <span class="icon-minus"></span>
                    {{labels.foldAll}}
            </a>
            <ul class="accordion accordion--gray">

                <!-- 1. Target group -->
                <li class="accordion__item" 
                    :class="{'accordion__item--open': isOpen('targetGroups')}">
                    <a tabindex="0" @click="toggle('targetGroups')" @keyup.enter="toggle('targetGroups')"  class="accordion__title"><b>1. {{labels.targetGroup}}</b></a>
                    <edit-study-form-elements 
                        :elements="draft.targetGroups" 
                        :name="'targetGroups'" 
                        :canEdit="canEdit"
                        @setInvalidState="setInvalidState($event, '1. ' + labels.targetGroup)">
                    </edit-study-form-elements>
                </li>

                <!-- 2. Target group backgrounds -->
                <li class="accordion__item" 
                    :class="{'accordion__item--open': isOpen('targetGroupBackgrounds')}">
                    <a tabindex="0" @click="toggle('targetGroupBackgrounds')" @keyup.enter="toggle('targetGroupBackgrounds')" class="accordion__title"><b>2. {{labels.backgroundCharacteristics}}</b></a>
                    <edit-study-form-elements 
                        :elements="draft.targetGroupBackgrounds" 
                        :name="'targetGroupBackgrounds'" 
                        :canEdit="canEdit"
                        @setInvalidState="setInvalidState($event, '2. ' + labels.backgroundCharacteristics)">
                    </edit-study-form-elements>
                </li>

                <!-- 3. Activities -->
                <li class="accordion__item" 
                    :class="{'accordion__item--open': isOpen('activities')}">
                    <a tabindex="0" @click="toggle('activities')" @keyup.enter="toggle('activities')" class="accordion__title"><b>3. {{labels.interventions}}</b></a>
                    <edit-study-form-elements 
                        :elements="draft.activities" 
                        :name="'activities'" 
                        :canEdit="canEdit"
                        @setInvalidState="setInvalidState($event, '3. ' + labels.interventions)">>
                    </edit-study-form-elements>
                </li>

                <!-- 4. Effects -->
                <li class="accordion__item" 
                    :class="{'accordion__item--open': isOpen('effects')}">
                    <a tabindex="0" @click="toggle('effects')" @keyup.enter="toggle('effects')" class="accordion__title"><b>4. {{labels.effect}}</b></a>
                    <edit-study-form-elements 
                        :elements="draft.effects" 
                        :name="'effects'" 
                        :canEdit="canEdit"
                        @setInvalidState="setInvalidState($event, '4. ' + labels.effect)">
                    </edit-study-form-elements>
                </li>
            </ul>
        </div>
        <div class="cell content--gray">
            <div class="validation__summary">
                <p v-if="errors.items.length > 0 || invalidAccordions.length > 0">
                    <b>{{labels.theFollowingMustBeCompleted}}</b>
                </p>
                <ul>
                    <li v-for="error in errors.items" :key="error.id">{{ error.msg }}</li>
                    <li v-for="(error, i) in invalidAccordions" :key="i">{{ error.label }} {{labels.mustBeFilled}}</li>
                </ul>
            </div>
            <p v-if="canEdit" class="form__buttons form__buttons--secondary text-center">
                <button tabindex="0" class="link--back float-left text--high text--blue" @click="close" @keyup.enter="close" ><span class="icon-circle-left2 icon--large"></span> {{labels.close}}</button>
                <button tabindex="0" @click="remove" @keyup.enter="remove" class="button secondary">{{labels.deleteResult}}</button>
                <button tabindex="0" @click="save" @keyup.enter="save" :disabled="errors.items.length > 0 || invalidAccordions.length > 0" class="button" :title="buttonTitle()">{{labels.saveAndClose}}</button>
            </p>
            <p v-else class="form__buttons form__buttons--secondary text-center">
                <button tabindex="0" @click="close" @keyup.enter="close" class="button">{{labels.closeResult}}</button>
            </p>
        </div>
    </div>
    </FocusLock>
    
</template>
<script>
import { each, isPlainObject, some, findIndex } from "lodash";

export default {
  props: ["result", "canEdit", "activeResult"],
  data() {
    return {
        draft: JSON.parse(JSON.stringify(this.result)),
        openAccordions: [],
        invalidAccordions: []
    };
  },
  methods: {
    save() {
        if (this.errors.items.length === 0 && this.invalidAccordions.length === 0) {
            this.draft.isNew = false;
            this.$emit('save', this.draft);
        }
    },
    close() {
        var cf = confirm(this.labels.areYouSureClose);
        if (cf) {
            this.$emit('close', { delete: this.draft.isNew, result: this.result });
        }
    },
    remove() {
        var cf = confirm(this.labels.areYouSureRemove);
        if (cf) {
            this.$emit('close', { delete: true, result: this.result });
        }
    },
    setInvalidState(data, label) {
        var item = {
            name: data.name,
            label: label
        };

        var idx = findIndex(this.invalidAccordions, item);
        var inArr = (idx !== -1);
        if (inArr && data.valid) {
            this.invalidAccordions.splice(idx, 1);
        } else if (!data.valid && !inArr) {
            this.invalidAccordions.push(item);
        }
    },
    isOpen(name) {
      return this.openAccordions.indexOf(name) !== -1;
    },
    hasErrors(name) {
        return some(this.invalidAccordions, ['name', name]);
    },
    toggle(name) {
      var idx = this.openAccordions.indexOf(name);
      if (idx !== -1) {
        this.openAccordions.splice(idx, 1);
      } else {
        this.openAccordions.push(name);
      }
    },
    toggleAll(open) {
      if (open) {
          each(this.draft, (item, key) => {
              if (isPlainObject(item) && this.openAccordions.indexOf(key)) this.openAccordions.push(key);
          })
      } else {
          this.openAccordions = [];
      }
    },
    showInvalid(name) {
        return this.errors.has(name);
    },
    buttonTitle() {
        if (this.errors.items.length > 0 || this.invalidAccordions.length > 0) return this.labels.resultCannotGetSavedButtonText;
    },
    counter(count, maxCount){
        return (count === 0) ? "" : count + '/' + maxCount + ' tegn'; 
    }
  },
  computed:{
       labels() {
        return this.$store.state.labels['pages.mypage.suggestStudyResult'] || undefined;
    }
  },
  mounted(){
      this.$store.dispatch('getLabels', { label: 'pages.mypage.suggestStudyResult' });
  }
};
</script>



// WEBPACK FOOTER //
// src/components/my-pages/StudyResult.vue